import { addToCart as addPartToCart } from '../Cart/StorefrontCart';
import { markSavedWishlistButtons, toggleWishlist } from '../Wishlist/WishlistClient';
import { showToast } from '../Toast/StorefrontToast';
import { formatMoney } from '../../Common/Helpers/FormatMoney';
import { sfUrl } from '../StorefrontPaths';
import { sizedImage } from '../StorefrontImages';

/**
 * Storefront catalog list — responsive product grid with a left-side
 * shop-by-category tree drawer (collapsible, slide-away) and search.
 *
 * Reads:  /Services/Storefront/Catalog/List, /Services/Storefront/Catalog/Categories
 * Writes: the saved cart via the StorefrontCart client
 */

interface PartCard {
    Id: number;
    PartId: string;
    Title: string;
    Slug: string;
    ImageUrl: string;
    Brand: string;
    CategoryName: string;
    AiDescription: string | null;
    Pills: string[];
    PartType: number | null;
    IsKitOrAssembly: boolean;
    Price: number | null;
    Quantity: number | null;
    FitsActiveVehicle: boolean;
}

interface ActiveVehicle { Year: number | null; Make: string; Model: string; }
interface ListResponse { Parts: PartCard[]; TotalCount: number; Skip: number; Take: number; ActiveVehicle: ActiveVehicle | null; }

interface SubCategoryNode { Id: number; Name: string; Count: number; }
interface CategoryNode { Id: number; Name: string; Count: number; SubCategories: SubCategoryNode[]; }
interface CategoryTreeResponse { Categories: CategoryNode[]; }

const PAGE_SIZE = 24;

// At this width storefront.css turns the category drawer into an off-canvas sheet opened
// from the sticky toolbar's "Categories" button (#36899). Keep in sync with the CSS.
const SHEET_MQ = window.matchMedia('(max-width: 800px)');

export class CatalogList {
    private host: HTMLElement;
    private parts: PartCard[] = [];
    private totalCount = 0;
    private skip = 0;
    private search = '';
    private isLoading = false;
    private activeVehicle: ActiveVehicle | null = null;
    private searchTimer: number | null = null;

    private tree: CategoryNode[] = [];
    private categoryId: number | null = null;
    private subCategoryId: number | null = null;
    private expanded = new Set<number>();

    constructor(host: HTMLElement) {
        this.host = host;

        // Initial filter/search from URL (?search= / ?categoryId= / ?subCategoryId=), so the
        // state survives leaving for a product and coming back.
        const params = new URLSearchParams(window.location.search);
        this.search = (params.get('search') || '').trim();
        const c = parseInt(params.get('categoryId') || '', 10);
        const s = parseInt(params.get('subCategoryId') || '', 10);
        if (!isNaN(c)) { this.categoryId = c; this.expanded.add(c); }
        if (!isNaN(s)) this.subCategoryId = s;

        this.renderShell();
        this.loadTree();
        this.fetch(true);

        window.addEventListener('sc-vehicle-changed', () => { this.skip = 0; this.parts = []; this.loadTree(); this.fetch(true); });
    }

    // ── Data ────────────────────────────────────────────────

    private async loadTree(): Promise<void> {
        try {
            // The tree's counts/visibility are scoped to the selected vehicle (resolved
            // server-side from the sc_vehicle cookie) — NOT to the active search, which
            // only ever filters All Parts. See #35985. The endpoint binds [FromBody], so
            // a Content-Type + JSON body is required even though the body is empty —
            // without it ASP.NET Core rejects the request with 415 Unsupported Media Type.
            const res = await fetch('/Services/Storefront/Catalog/Categories', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });
            const data: CategoryTreeResponse = await res.json();
            this.tree = data.Categories || [];
            this.renderTree();
        } catch { /* tree optional */ }
    }

    private async fetch(replace: boolean): Promise<void> {
        if (this.isLoading) return;
        this.isLoading = true;
        this.renderState();
        try {
            const res = await fetch('/Services/Storefront/Catalog/List', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Take: PAGE_SIZE, Skip: this.skip,
                    // A stored search only ever filters "All Parts". An individually
                    // selected category/subcategory always shows its full vehicle-
                    // compatible set — the search is remembered (kept in `this.search`
                    // and the URL) but withheld here so it doesn't further restrict
                    // category results. See #35985.
                    Search: (this.categoryId || this.subCategoryId) ? null : (this.search || null),
                    CategoryId: this.categoryId, SubCategoryId: this.subCategoryId
                })
            });
            const data: ListResponse = await res.json();
            this.parts = replace ? (data.Parts || []) : this.parts.concat(data.Parts || []);
            this.totalCount = data.TotalCount ?? 0;
            this.activeVehicle = data.ActiveVehicle ?? null;
        } catch { /* keep parts */ }
        finally { this.isLoading = false; this.renderState(); }
    }

    private applyFilter(categoryId: number | null, subCategoryId: number | null): void {
        this.categoryId = categoryId;
        this.subCategoryId = subCategoryId;
        this.skip = 0;
        this.parts = [];

        this.syncUrl();
        this.renderTree();
        this.closeSheet();
        this.fetch(true);
    }

    /// Reflect the current search + category filters in the URL so the page state
    /// survives navigating into a product and coming back (and is shareable).
    private syncUrl(): void {
        const params = new URLSearchParams();
        if (this.search) params.set('search', this.search);
        if (this.categoryId) params.set('categoryId', String(this.categoryId));
        if (this.subCategoryId) params.set('subCategoryId', String(this.subCategoryId));
        const qs = params.toString();
        history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
    }

    // ── Shell ───────────────────────────────────────────────

    private renderShell(): void {
        this.host.innerHTML = `
            <div class="sf-catalog-layout">
                <button type="button" class="sf-drawer-handle" data-action="toggle-drawer" title="Show/hide categories">
                    <i class="fa fa-bars"></i> Categories
                </button>
                <div class="sf-cat-overlay" data-action="close-sheet"></div>
                <aside class="sf-cat-drawer" data-region="drawer" aria-label="Shop by Category">
                    <div class="sf-cat-drawer-head">
                        <span>Shop by Category</span>
                        <button type="button" class="sf-cat-close" data-action="toggle-drawer" aria-label="Hide categories"><i class="fa fa-angle-double-left"></i></button>
                    </div>
                    <div class="sf-cat-tree" data-region="tree"><div class="sf-loading">Loading…</div></div>
                </aside>
                <div class="sf-catalog-shell">
                    <div class="sf-catalog-toolbar">
                        <button type="button" class="sf-cat-open" data-action="open-sheet" aria-expanded="false">
                            <i class="fa fa-sliders"></i> Categories
                        </button>
                        <div class="sf-catalog-summary" data-region="summary"></div>
                    </div>
                    <div class="sf-catalog-grid" data-region="grid"></div>
                    <div class="sf-catalog-footer" data-region="footer"></div>
                </div>
            </div>
        `;

        // No in-page search box — searches come from the header bar (?search=) and the AI
        // assistant. Any ?search from the header is read in the constructor and applied.
        // The header field itself keeps the submitted term visible and offers a clear
        // control (see _StorefrontLayout.cshtml + VehiclePickerPage.ts).
        this.host.querySelectorAll('[data-action="toggle-drawer"]').forEach(b =>
            b.addEventListener('click', () => {
                // On phones the drawer's close button dismisses the sheet instead.
                if (SHEET_MQ.matches) { this.closeSheet(); return; }
                const layout = this.host.querySelector('.sf-catalog-layout');
                layout?.classList.toggle('drawer-collapsed');
            }));
        this.host.querySelector('[data-action="open-sheet"]')?.addEventListener('click', () => this.openSheet());
        this.host.querySelector('[data-action="close-sheet"]')?.addEventListener('click', () => this.closeSheet());
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeSheet(); });
        SHEET_MQ.addEventListener('change', (e) => { if (!e.matches) this.closeSheet(); });
    }

    // ── Mobile category sheet ───────────────────────────────

    private openSheet(): void {
        this.host.querySelector('.sf-catalog-layout')?.classList.add('sheet-open');
        this.host.querySelector('.sf-cat-open')?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('sf-sheet-open');
        window.setTimeout(() => this.host.querySelector<HTMLElement>('.sf-cat-close')?.focus(), 60);
    }

    private closeSheet(): void {
        const layout = this.host.querySelector('.sf-catalog-layout');
        if (!layout?.classList.contains('sheet-open')) return;
        layout.classList.remove('sheet-open');
        this.host.querySelector('.sf-cat-open')?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('sf-sheet-open');
    }

    // ── Category tree ───────────────────────────────────────

    private renderTree(): void {
        // Dot on the mobile "Categories" button while a category filter is applied.
        this.host.querySelector('.sf-cat-open')?.classList.toggle('has-filter', !!this.categoryId);
        const el = this.host.querySelector('[data-region="tree"]');
        if (!el) return;
        if (this.tree.length === 0) { el.innerHTML = '<div class="sf-cat-empty">No categories.</div>'; return; }

        const allActive = !this.categoryId && !this.subCategoryId ? ' is-active' : '';
        let html = `<button type="button" class="sf-cat-all${allActive}" data-all="1">All Parts</button>`;

        for (const cat of this.tree) {
            const isExpanded = this.expanded.has(cat.Id);
            const catActive = this.categoryId === cat.Id && !this.subCategoryId ? ' is-active' : '';
            const hasSubs = cat.SubCategories && cat.SubCategories.length > 0;
            html += `
                <div class="sf-cat-node">
                    <div class="sf-cat-row${catActive}">
                        ${hasSubs ? `<button type="button" class="sf-cat-toggle" data-toggle="${cat.Id}" aria-label="Expand"><i class="fa fa-angle-${isExpanded ? 'down' : 'right'}"></i></button>` : '<span class="sf-cat-toggle-spacer"></span>'}
                        <button type="button" class="sf-cat-link" data-cat="${cat.Id}">
                            <span class="sf-cat-name">${this.escape(cat.Name)}</span>
                            <span class="sf-cat-count">${cat.Count}</span>
                        </button>
                    </div>
                    ${hasSubs && isExpanded ? `<div class="sf-cat-subs">${cat.SubCategories.map(sub => {
                const subActive = this.subCategoryId === sub.Id ? ' is-active' : '';
                return `<button type="button" class="sf-subcat-link${subActive}" data-cat="${cat.Id}" data-sub="${sub.Id}">
                            <span class="sf-cat-name">${this.escape(sub.Name)}</span><span class="sf-cat-count">${sub.Count}</span>
                        </button>`;
            }).join('')}</div>` : ''}
                </div>`;
        }
        el.innerHTML = html;

        el.querySelector('[data-all]')?.addEventListener('click', () => this.applyFilter(null, null));
        el.querySelectorAll('[data-toggle]').forEach(b => b.addEventListener('click', (e) => {
            const id = parseInt((e.currentTarget as HTMLElement).getAttribute('data-toggle') || '0', 10);
            if (this.expanded.has(id)) this.expanded.delete(id); else this.expanded.add(id);
            this.renderTree();
        }));
        el.querySelectorAll('.sf-cat-link').forEach(b => b.addEventListener('click', (e) => {
            const id = parseInt((e.currentTarget as HTMLElement).getAttribute('data-cat') || '0', 10);
            this.expanded.add(id);
            this.applyFilter(id, null);
        }));
        el.querySelectorAll('.sf-subcat-link').forEach(b => b.addEventListener('click', (e) => {
            const t = e.currentTarget as HTMLElement;
            this.applyFilter(parseInt(t.getAttribute('data-cat') || '0', 10), parseInt(t.getAttribute('data-sub') || '0', 10));
        }));
    }

    // ── Grid ────────────────────────────────────────────────

    private renderState(): void { this.renderSummary(); this.renderGrid(); this.renderFooter(); }

    private renderSummary(): void {
        const el = this.host.querySelector('[data-region="summary"]');
        if (!el) return;
        if (this.isLoading && this.parts.length === 0) { el.innerHTML = '<span class="sf-catalog-summary-text">Loading…</span>'; return; }
        let summary = '';
        if (this.activeVehicle && this.activeVehicle.Year && this.activeVehicle.Make && this.activeVehicle.Model) {
            summary += `<span class="sf-catalog-fits">Fits ${this.escape(this.activeVehicle.Year + ' ' + this.activeVehicle.Make + ' ' + this.activeVehicle.Model)}</span> · `;
        }
        summary += `<span class="sf-catalog-count">${this.totalCount.toLocaleString()} ${this.totalCount === 1 ? 'part' : 'parts'}</span>`;
        el.innerHTML = summary;
    }

    private renderGrid(): void {
        const el = this.host.querySelector('[data-region="grid"]');
        if (!el) return;
        if (!this.isLoading && this.parts.length === 0) {
            el.innerHTML = `<div class="sf-empty"><h2>No results found</h2><p>We couldn't find any in-stock parts matching your search. Try a different part name, SKU, OEM number, category, or vehicle.</p></div>`;
            return;
        }
        el.innerHTML = this.parts.map(p => this.renderCard(p)).join('');
        markSavedWishlistButtons(el); // red heart on parts already in the wishlist
        el.querySelectorAll('[data-action="add-to-cart"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const id = parseInt((e.currentTarget as HTMLElement).getAttribute('data-id') || '0', 10);
                const part = this.parts.find(p => p.Id === id);
                if (part) this.addToCart(part);
            });
        });
        el.querySelectorAll('[data-action="add-to-wishlist"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const id = parseInt((e.currentTarget as HTMLElement).getAttribute('data-id') || '0', 10);
                this.addToWishlist(id, e.currentTarget as HTMLButtonElement);
            });
        });
    }

    private renderCard(p: PartCard): string {
        const inStock = (p.Quantity ?? 0) > 0;
        const stockText = inStock ? `In Stock` : `Out of Stock`;
        const price = p.Price != null ? formatMoney(p.Price) : '—';
        const meta = [p.Brand, p.CategoryName].filter(Boolean).map(s => this.escape(s)).join(' · ');
        const href = p.Slug ? sfUrl('/product/' + encodeURIComponent(p.Slug)) : null;
        const title = href
            ? `<h2 class="sf-card-heading"><a class="sf-card-title-link" href="${this.escapeAttr(href)}">${this.escape(p.Title)}</a></h2>`
            : `<h2 class="sf-card-heading">${this.escape(p.Title)}</h2>`;
        const kitBadge = p.IsKitOrAssembly
            ? `<div class="sf-kit-badge">${p.PartType === 16 ? 'Assembly' : (p.PartType === 8 ? 'Set' : 'Kit')}</div>` : '';
        const fitsBadge = p.FitsActiveVehicle ? `<div class="sf-fits-badge"><i class="fa fa-check"></i> Fits</div>` : '';
        const imageInner = `<img src="${this.escapeAttr(sizedImage(p.ImageUrl, 'card'))}" alt="${this.escapeAttr(p.Title)}" loading="lazy" />${kitBadge}${fitsBadge}`;
        const image = href ? `<a class="sf-card-image" href="${this.escapeAttr(href)}">${imageInner}</a>` : `<div class="sf-card-image">${imageInner}</div>`;

        return `
            <article class="sf-card" data-part-id="${p.Id}">
                ${image}
                <div class="sf-card-body">
                    ${title}
                    ${(p.Pills && p.Pills.length) ? `<div class="sf-pills">${p.Pills.map(pill => `<span class="sf-pill${/capa/i.test(pill) ? ' capa' : ''}">${this.escape(pill)}</span>`).join('')}</div>` : ''}
                    ${p.AiDescription ? `<p class="sf-card-desc">${this.escape(p.AiDescription)}</p>` : ''}
                    ${meta ? `<div class="sf-card-meta">${meta}</div>` : ''}
                    <div class="sf-card-bottom">
                        <div class="sf-card-price-row">
                            <div class="sf-card-price">${price}</div>
                            <div class="sf-card-stock ${inStock ? '' : 'out'}">${stockText}</div>
                        </div>
                        <div class="sf-card-actions">
                            <button type="button" class="sf-card-add" data-action="add-to-cart" data-id="${p.Id}" ${inStock ? '' : 'disabled'}>
                                ${inStock ? 'Add to Cart' : 'Out of Stock'}
                            </button>
                            <button type="button" class="sf-wishlist-icon-btn" data-action="add-to-wishlist" data-id="${p.Id}" aria-label="Add to Wishlist" title="Add to Wishlist">
                                <i class="fa fa-heart-o"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </article>`;
    }

    private renderFooter(): void {
        const el = this.host.querySelector('[data-region="footer"]');
        if (!el) return;
        const hasMore = this.parts.length < this.totalCount;
        if (this.isLoading && this.parts.length > 0) el.innerHTML = '<div class="sf-loading">Loading more…</div>';
        else if (hasMore) {
            el.innerHTML = `<button type="button" class="sf-btn sf-btn-secondary sf-load-more" data-action="load-more">Load more parts</button>`;
            el.querySelector('[data-action="load-more"]')?.addEventListener('click', () => { this.skip += PAGE_SIZE; this.fetch(false); });
        } else el.innerHTML = '';
    }

    private async addToCart(part: PartCard): Promise<void> {
        const btn = this.host.querySelector(`[data-action="add-to-cart"][data-id="${part.Id}"]`) as HTMLButtonElement;
        if (btn) btn.disabled = true;
        try {
            await addPartToCart(part.Id, 1);
        } catch (e) {
            if (btn) btn.disabled = false;
            showToast((e as Error).message, 'error');
            return;
        }
        if (!btn) return;
        const original = btn.textContent;
        btn.textContent = 'Added ✓'; btn.classList.add('is-added');
        window.setTimeout(() => { btn.textContent = original; btn.disabled = false; btn.classList.remove('is-added'); }, 1200);
    }

    private async addToWishlist(partId: number, btn: HTMLButtonElement): Promise<void> {
        await toggleWishlist(btn, partId, 1); // red heart = saved; clicking it again removes
    }

    private escape(s: string | null | undefined): string {
        if (s == null) return '';
        return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
    }
    private escapeAttr(s: string | null | undefined): string { return this.escape(s); }
}
